import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

export const getCommissionReport = createAsyncThunk('COMMISSION REPORT', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(`${URL.GET_AGENCY_COMMISSION}?date=${data}`, data, thunkAPI);
  return response.data;
});

export const getCommissionBookings = createAsyncThunk(
  'GET COMMISSION BOOKINGS',
  async ({ accountId, productType, date, page = 1, limit = 100 }, thunkAPI) => {
    const params = new URLSearchParams({
      date,
      product_type: productType,
      page: String(page),
      limit: String(limit),
    });
    const response = await axiosAdmin.get(
      `${URL.GET_COMMISSION_BOOKINGS}/${accountId}/commission-bookings?${params.toString()}`,
      thunkAPI
    );
    return {
      accountId,
      productType,
      date,
      data: response.data,
    };
  }
);

const extractCommissionBookingRecords = (payload) => {
  const root = payload?.data ?? payload;
  if (!root) return [];

  if (Array.isArray(root)) return root;

  if (typeof root === 'object') {
    const bookingRecords = root.booking_records ?? root.BookingRecords;
    if (Array.isArray(bookingRecords)) return bookingRecords;

    const transactions = root.Transactions ?? root.transactions;
    if (Array.isArray(transactions)) return transactions;

    const nested = root.data ?? root.bookings ?? root.records;
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === 'object') {
      const nestedRecords =
        nested.booking_records ??
        nested.BookingRecords ??
        nested.Transactions ??
        nested.transactions;
      if (Array.isArray(nestedRecords)) return nestedRecords;
    }
  }

  return [];
};

const initialState = {
  isLoading: false,
  commissionReportData: [],
  commissionBookingsLoading: false,
  commissionBookingsKey: null,
  commissionBookingsTransactions: [],
};

export const commissionReportSlice = createSlice({
  name: 'commission',
  initialState,
  reducers: {
    clearCommissionBookings: (state) => {
      state.commissionBookingsLoading = false;
      state.commissionBookingsKey = null;
      state.commissionBookingsTransactions = [];
    },
  },
  extraReducers: {
    [getCommissionReport.pending]: (state) => {
      state.isLoading = true;
      state.commissionReportData = [];
    },
    [getCommissionReport.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.commissionReportData = action.payload.data;
    },
    [getCommissionReport.rejected]: (state, action) => {
      state.isLoading = false;
      state.commissionReportData = [];
      toast.error(action?.payload?.message);
    },
    [getCommissionBookings.pending]: (state, action) => {
      const { accountId, productType } = action.meta.arg;
      state.commissionBookingsLoading = true;
      state.commissionBookingsKey = `${accountId}-${productType}`;
      state.commissionBookingsTransactions = [];
    },
    [getCommissionBookings.fulfilled]: (state, action) => {
      const { accountId, productType } = action.payload;
      state.commissionBookingsLoading = false;
      state.commissionBookingsKey = `${accountId}-${productType}`;
      state.commissionBookingsTransactions = extractCommissionBookingRecords(action.payload.data);
    },
    [getCommissionBookings.rejected]: (state, action) => {
      state.commissionBookingsLoading = false;
      state.commissionBookingsTransactions = [];
      const message =
        action?.payload?.error || action?.payload?.message || 'Failed to load commission bookings';
      toast.error(message);
    },
  },
});

export const { clearCommissionBookings } = commissionReportSlice.actions;

export default commissionReportSlice.reducer;
